import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { setBoot } from "../boot";
import { updatePeriod } from "../period";
import { getCurrentPeriod } from "../../common/util/period";
import type { CustomersResponse } from "../../services/Customer/CustomerList/customerListAPI.types";

export const CUSTOMER_FILTER_IDS = {
  VERIFIED: "verified",
  NO_VERIFIED: "noVerified",
  NEW_CUSTOMER: "newCustomer",
  CUSTOMER: "customer",
  MEMBER: "member",
  GUEST: "guest",
  SHAREBAR: "sharebar",
  CURRENT_RENEWAL_CUSTOMER: "isCurrentMonthRenewalDate",
};

export const CUSTOMER_FILTER_GROUPS = {
  IS_VERIFIED: "isVerified",
  IS_NEW: "isNew",
  BUSINESS_NATURE: "businessNature",
  IS_CURRENT_MONTH_RENEWAL_DATE: "isCurrentMonthRenewalDate",
};

export interface FilterOption {
  id: string;
  group: string;
  checked: boolean;
  enable: boolean;
  quickFilter: boolean;
  value: boolean | string | number;
}

export interface FilterState {
  filters: FilterOption[];
  showFilterMenu: boolean;
  searchKeyword: string;
}

interface EnabledFilterConfig {
  id: string;
  group: string;
  value: boolean | string | number;
  quickFilter: boolean;
}

export interface ApiFilterPayload {
  group: string;
  id: string;
  checked: boolean;
  value: boolean | string | number;
}

const initialState: FilterState = {
  filters: [],
  showFilterMenu: false,
  searchKeyword: "",
};

const { actions, reducer } = createSlice({
  name: "customerFilter",
  initialState,
  reducers: {
    toggleFilter: (state, action: PayloadAction<string>) => {
      const filterId = action.payload;
      state.filters = state.filters.map((filter) =>
        filter.id === filterId ? { ...filter, checked: !filter.checked } : filter,
      );
    },
    updateFilters: (state, action: PayloadAction<FilterOption[]>) => {
      const updatedFilters = action.payload;
      state.filters = state.filters.map((filter) => {
        const updated = updatedFilters.find((f) => f.id === filter.id);
        return updated ? { ...filter, checked: updated.checked } : filter;
      });
    },
    resetFilters: (state) => {
      state.filters = state.filters.map((filter) => ({ ...filter, checked: false }));
    },
    toggleFilterMenu: (state) => {
      state.showFilterMenu = !state.showFilterMenu;
    },
    updateSearchKeyword: (state, action: PayloadAction<string>) => {
      state.searchKeyword = action.payload;
    },
    selectFilter: (state, action: PayloadAction<string>) => {
      const filterId = action.payload;
      state.filters = state.filters.map((filter) => ({
        ...filter,
        checked: filter.id === filterId,
      }));
    },
  },
  extraReducers: (builder) => {
    // Initialize filters from boot config (single source of truth)
    builder.addCase(setBoot, (state, { payload }) => {
      const { enabledFilters = [] } = payload.configuration.globalCML.customerList;
      state.filters = (enabledFilters as EnabledFilterConfig[]).map((config) => ({
        id: config.id,
        group: config.group,
        value: config.value,
        quickFilter: config.quickFilter ?? false,
        checked: false,
        enable: config.group !== CUSTOMER_FILTER_GROUPS.IS_VERIFIED,
      }));
    });

    // Toggle isCurrentMonthRenewalDate filters based on selected period
    builder.addCase(updatePeriod, (state, { payload }) => {
      const isCurrentMonth = getCurrentPeriod() === payload.period;
      state.filters = state.filters.map((filter) =>
        filter.group === CUSTOMER_FILTER_GROUPS.IS_CURRENT_MONTH_RENEWAL_DATE
          ? { ...filter, enable: isCurrentMonth, checked: isCurrentMonth ? filter.checked : false }
          : filter,
      );
    });

    // Toggle isVerified filters based on ShowCSIInfo from customer list API response
    builder.addMatcher(
      (action) => action.type === "customers/updateCustomerList",
      (state, action: PayloadAction<{ requestId: string; data: CustomersResponse }>) => {
        const isCsiActive = action.payload.data.ShowCSIInfo ?? false;
        state.filters = state.filters.map((filter) =>
          filter.group === CUSTOMER_FILTER_GROUPS.IS_VERIFIED
            ? { ...filter, enable: isCsiActive, checked: isCsiActive ? filter.checked : false }
            : filter,
        );
      },
    );
  },
});

export const { toggleFilter, updateFilters, resetFilters, toggleFilterMenu, updateSearchKeyword, selectFilter } =
  actions;

// Memoized selectors
const selectFilters = (state: { customerFilter: FilterState }): FilterOption[] => state.customerFilter.filters;

export const selectFilterMenu = createSelector([selectFilters], (filters) => filters.filter((f) => f.enable));

export const selectQuickFilterMenu = createSelector([selectFilters], (filters) =>
  filters.filter((f) => f.enable && f.quickFilter),
);

export const selectFilterCount = createSelector(
  [selectFilters],
  (filters) => filters.filter((f) => f.checked && f.enable).length,
);

export const selectIsReset = createSelector([selectFilters], (filters) => filters.every((f) => !f.checked));

export const selectFiltersForApi = createSelector([selectFilters], (filters) =>
  filters.filter((f) => f.enable).map(({ group, id, checked, value }) => ({ group, id, checked, value })),
);

export default reducer;
